import React from 'react';
import { View, TouchableOpacity, StyleSheet, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ConnectionType } from '../../types/network';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface ConnectionTypeModalProps {
  visible: boolean;
  userName: string;
  mode: 'send' | 'accept';
  onSelect: (type: ConnectionType) => void;
  onCancel: () => void;
}

const ConnectionTypeModal: React.FC<ConnectionTypeModalProps> = ({
  visible,
  userName,
  mode,
  onSelect,
  onCancel,
}) => {
  const { theme } = useTheme();

  const connectionTypes: { type: ConnectionType; label: string; icon: string; color: string; description: string }[] = [
    {
      type: 'mentor',
      label: 'Mentor',
      icon: 'school-outline',
      color: '#4CAF50',
      description: 'Someone who guides and advises you',
    },
    {
      type: 'client',
      label: 'Client',
      icon: 'briefcase-outline',
      color: '#2196F3',
      description: 'A business client or customer',
    },
    {
      type: 'supplier',
      label: 'Supplier',
      icon: 'cube-outline',
      color: '#FF9800',
      description: 'A supplier or service provider',
    },
    {
      type: 'others',
      label: 'Others',
      icon: 'person-outline',
      color: '#9C27B0',
      description: 'Other professional connection',
    },
  ];

  const getTitle = () => {
    if (mode === 'send') {
      return `Connect with ${userName}`;
    }
    return `Accept ${userName}'s Request`;
  };

  const getSubtitle = () => {
    if (mode === 'send') {
      return 'How would you categorize this connection?';
    }
    return 'How would you categorize this connection?';
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
          <View style={[styles.modalHeader, { borderBottomColor: theme.border }]}>
            <View>
              <ThemedText style={[styles.modalTitle, { color: theme.text }]}>
                {getTitle()}
              </ThemedText>
              <ThemedText style={[styles.modalSubtitle, { color: theme.textSecondary }]}>
                {getSubtitle()}
              </ThemedText>
            </View>
            <TouchableOpacity onPress={onCancel} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={theme.textSecondary} />
            </TouchableOpacity>
          </View>

          <View style={styles.modalBody}>
            {connectionTypes.map((item) => (
              <TouchableOpacity
                key={item.type}
                style={[styles.typeOption, { backgroundColor: theme.backgroundSecondary, borderColor: theme.border }]}
                onPress={() => onSelect(item.type)}
                activeOpacity={0.7}
              >
                <View style={[styles.typeIcon, { backgroundColor: item.color + '20' }]}>
                  <Ionicons name={item.icon as any} size={24} color={item.color} />
                </View>
                <View style={styles.typeInfo}>
                  <ThemedText style={[styles.typeLabel, { color: theme.text }]}>
                    {item.label}
                  </ThemedText>
                  <ThemedText style={[styles.typeDescription, { color: theme.textSecondary }]}>
                    {item.description}
                  </ThemedText>
                </View>
                <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
              </TouchableOpacity>
            ))}
          </View>

          <View style={[styles.modalFooter, { borderTopColor: theme.border }]}>
            <TouchableOpacity
              style={[styles.cancelButton, { backgroundColor: theme.backgroundSecondary }]}
              onPress={onCancel}
              activeOpacity={0.8}
            >
              <ThemedText style={[styles.cancelButtonText, { color: theme.text }]}>
                Cancel
              </ThemedText>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 14,
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    padding: 16,
    gap: 12,
  },
  typeOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  typeIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  typeInfo: {
    flex: 1,
  },
  typeLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  typeDescription: {
    fontSize: 13,
  },
  modalFooter: {
    padding: 16,
    borderTopWidth: 1,
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
});

export default ConnectionTypeModal;
